import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { currentDateFnsLocale } from "../../../../i18n/dateLocales";
import {
  batchUpsertHours,
  createHour,
  deleteHour,
  fetchHours,
  updateHour,
} from "../../../../api/hour";
import { fetchProjects } from "../../../../api/project";
import {
  fetchProjectActivities,
  fetchProjectActivityGroups,
} from "../../../../api/project_activity";
import {
  buildActivityGroupSections,
  hasRealActivityGroups,
} from "../../../utils/moduleSelectors/projectActivityGroupUtils";
import { fetchUserWorkSchedule } from "../../../../api/user";
import {
  QuickListPicker,
  activityPickerItem,
  projectPickerItem,
} from "./QuickListPicker";
import {
  AUTOSAVE_DEBOUNCE_MS,
  CELL_STATUS,
  activityTitleOf,
  buildRowsFromHours,
  dayHeaderParts,
  formatHoursDisplay,
  formatHoursSum,
  formatWeekLabel,
  makeEmptyRow,
  normalHoursForWeek,
  parseHoursInput,
  projectMetaOf,
  projectTitleOf,
  pushRecentRow,
  readRecentRows,
  rowKeyFromIds,
  shiftWeek,
  weekBounds,
} from "./quickEntryUtils";
import "./hours-quick-entry.css";

async function fetchAllHoursPaginated(baseParams) {
  const per_page = 200;
  let page = 1;
  let all = [];
  let totalCount = null;
  for (;;) {
    const response = await fetchHours({ ...baseParams, per_page, page });
    const batch = response?.data || [];
    if (totalCount == null) totalCount = response?.totalCount ?? batch.length;
    all = all.concat(batch);
    if (batch.length < per_page || all.length >= totalCount) break;
    page += 1;
  }
  return all;
}

function hourJsonId(h) {
  return h?.id ?? h?.data?.id ?? null;
}

function hourJsonTime(h) {
  const t = h?.time ?? h?.data?.time;
  const n = parseFloat(t);
  return Number.isNaN(n) ? null : n;
}

function hourJsonDate(h) {
  return String(h?.date ?? h?.data?.date ?? "").substring(0, 10);
}

function hourJsonPaId(h) {
  return h?.project_activity?.id ?? h?.project_activity_id ?? h?.data?.project_activity_id ?? null;
}

/**
 * Desktop «Rask» weekly time grid (design: Hurtigregistrering).
 */
export function HoursQuickEntry({
  user,
  userId,
  onOpenHour,
  initialDate = null,
  refreshToken = null,
}) {
  const { t } = useTranslation();
  const currentUser = useSelector((s) => s.session.currentUser);
  const timeRegistrationFormat =
    user?.time_registration_format ||
    currentUser?.time_registration_format ||
    "hours_minutes";
  const useDecimal = timeRegistrationFormat === "decimal";

  const [anchor, setAnchor] = useState(() =>
    initialDate ? String(initialDate).substring(0, 10) : format(new Date(), "yyyy-MM-dd")
  );
  const { from, to, days } = useMemo(() => weekBounds(anchor), [anchor]);
  const dayKeys = useMemo(() => days.map((d) => format(d, "yyyy-MM-dd")), [days]);
  const dayMeta = useMemo(() => days.map((d) => dayHeaderParts(d, currentDateFnsLocale())), [days]);
  const weekMeta = useMemo(() => formatWeekLabel(from, to, currentDateFnsLocale()), [from, to]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scaledWeekdays, setScaledWeekdays] = useState(null);
  const [surfaceStatus, setSurfaceStatus] = useState("idle"); // idle | saving | saved | error
  const [batchUnavailable, setBatchUnavailable] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState(() => readRecentRows());
  const [projectHits, setProjectHits] = useState([]);
  const [focus, setFocus] = useState(null); // { rowId, dateKey }
  const [noteOpen, setNoteOpen] = useState(null); // { rowId, dateKey, top, left }
  const [noteDraft, setNoteDraft] = useState("");
  // Custom Quick pickers: mode = activity | project | new-activity
  const [picker, setPicker] = useState(null);
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [addNotice, setAddNotice] = useState(null);
  const [fill, setFill] = useState(null); // { rowId, dateKey, rangeDates }
  const [copyBusy, setCopyBusy] = useState(false);

  const rowsRef = useRef(rows);
  const pendingRef = useRef(new Map()); // key -> entry payload + meta
  const inFlightRef = useRef(new Map());
  const unresolvedRef = useRef(new Set());
  const timerRef = useRef(null);
  const savingRef = useRef(false);
  const gridRef = useRef(null);
  const flushRef = useRef(async () => {});

  useLayoutEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const scheduleFlush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      flushRef.current();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, []);

  const weekTotal = useMemo(() => {
    return rows.reduce((sum, row) => {
      return (
        sum +
        dayKeys.reduce((s, d) => s + (parseFloat(row.cells[d]?.time) || 0), 0)
      );
    }, 0);
  }, [rows, dayKeys]);

  const dayTotals = useMemo(() => {
    return dayKeys.map((d) =>
      rows.reduce((s, row) => s + (parseFloat(row.cells[d]?.time) || 0), 0)
    );
  }, [rows, dayKeys]);

  const normHours = useMemo(
    () => normalHoursForWeek(days, scaledWeekdays),
    [days, scaledWeekdays]
  );

  const loadWeek = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [hours, ws] = await Promise.all([
        fetchAllHoursPaginated({
          date_from: format(from, "yyyy-MM-dd"),
          date_to: format(to, "yyyy-MM-dd"),
          filters: [{ name: "USER", value: userId }],
        }),
        fetchUserWorkSchedule(userId),
      ]);
      const sw =
        ws && typeof ws === "object" && ws.scaled_weekdays
          ? ws.scaled_weekdays
          : null;
      setScaledWeekdays(sw);
      unresolvedRef.current.clear();
      setRows(buildRowsFromHours(hours, dayKeys));
      setSurfaceStatus("idle");
    } catch (e) {
      setSurfaceStatus("error");
    } finally {
      setLoading(false);
    }
  }, [userId, from, to, dayKeys]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  useEffect(() => {
    if (refreshToken == null) return;
    loadWeek();
  }, [refreshToken, loadWeek]);

  useEffect(() => {
    if (!addOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchProjects({
          query: query || undefined,
          per_page: 20,
          page: 1,
          filters: [{ name: "active", value: true }],
        });
        if (!cancelled) setProjectHits(res?.data || []);
      } catch {
        if (!cancelled) setProjectHits([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addOpen, query]);

  const updateCell = useCallback((rowId, dateKey, patch) => {
    const next = rowsRef.current.map((row) => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          cells: {
            ...row.cells,
            [dateKey]: { ...row.cells[dateKey], ...patch },
          },
        };
      });
    rowsRef.current = next;
    setRows(next);
  }, []);

  const queueEntry = useCallback(
    (row, dateKey, { time, description, destroy }) => {
      if (!row.project_id || !row.project_activity_id) return;
      const cell = row.cells[dateKey];
      if (cell?.conflictHours) return;
      const key = `${row.id}|${dateKey}`;
      // A lost response may have committed. Never retry a create blindly.
      if (unresolvedRef.current.has(key)) return;
      if (destroy && !cell?.hourId && !inFlightRef.current.has(key)) {
        pendingRef.current.delete(key);
        return;
      }
      pendingRef.current.set(key, {
        key,
        rowId: row.id,
        dateKey,
        entry: {
          id: cell?.hourId || null,
          date: dateKey,
          project_id: row.project_id,
          project_activity_id: row.project_activity_id,
          time: destroy ? 0 : time,
          description:
            description != null ? description : cell?.description || "",
          _destroy: !!destroy,
        },
      });
      updateCell(row.id, dateKey, {
        status: CELL_STATUS.idle,
        error: null,
      });
      setSurfaceStatus("idle");
      scheduleFlush();
    },
    [updateCell, scheduleFlush]
  );

  const fallbackUpsert = async (items) => {
    const saved = [];
    const errors = [];
    for (let i = 0; i < items.length; i += 1) {
      const { entry } = items[i];
      try {
        if (entry._destroy) {
          if (entry.id) {
            await deleteHour(entry.id);
            saved.push({ id: entry.id, date: entry.date, _destroyed: true });
          }
          continue;
        }
        const payload = {
          id: entry.id || undefined,
          date: entry.date,
          project_id: entry.project_id,
          project_activity_id: entry.project_activity_id,
          time: entry.time,
          description: entry.description || "",
          user_id: userId,
        };
        const res = entry.id
          ? await updateHour(payload)
          : await createHour(payload);
        if (res?.id || res?.data?.id) {
          saved.push(res?.data || res);
        } else if (res?.errors || res?.response?.status >= 400) {
          errors.push({
            index: i,
            errors: res?.errors || [t("hours.quick.save_error")],
          });
        } else {
          saved.push(res);
        }
      } catch (e) {
        errors.push({
          index: i,
          errors: [e?.message || t("hours.quick.save_error")],
        });
      }
    }
    return { saved, errors, unavailable: false };
  };

  const flush = useCallback(async () => {
    if (savingRef.current) return;
    const items = Array.from(pendingRef.current.values());
    if (!items.length) return;
    savingRef.current = true;
    inFlightRef.current = new Map(items.map((item) => [item.key, item]));
    pendingRef.current.clear();
    setSurfaceStatus("saving");
    items.forEach(({ rowId, dateKey }) => {
      updateCell(rowId, dateKey, { status: CELL_STATUS.saving, error: null });
    });

    try {
      let result = await batchUpsertHours({
        entries: items.map((i) => i.entry),
        user_id: userId,
      });

      if (result.unavailable) {
        setBatchUnavailable(true);
        result = await fallbackUpsert(items);
      }

      // Top-level 403 (e.g. not allowed to enter for this user) — mark all pending cells.
      if (result.status === 403 && (result.errors || []).length) {
        const msg = Array.isArray(result.errors[0]?.errors)
          ? result.errors[0].errors.join(", ")
          : t("hours.quick.save_error");
        items.forEach(({ key, rowId, dateKey }) => {
          pendingRef.current.delete(key);
          updateCell(rowId, dateKey, {
            status: CELL_STATUS.error,
            error: msg,
          });
        });
        setSurfaceStatus("error");
        return;
      }

      const errorByIndex = new Map(
        (result.errors || []).map((e) => [e.index, e.errors])
      );
      const savedList = result.saved || [];

      items.forEach((item, index) => {
        const err = errorByIndex.get(index);
        if (err) {
          pendingRef.current.delete(item.key);
          updateCell(item.rowId, item.dateKey, {
            status: CELL_STATUS.error,
            error: Array.isArray(err) ? err.join(", ") : String(err),
          });
          return;
        }

        if (item.entry._destroy) {
          if (!savedList.some((hour) => String(hourJsonId(hour)) === String(item.entry.id))) {
            unresolvedRef.current.add(item.key);
            pendingRef.current.delete(item.key);
            updateCell(item.rowId, item.dateKey, {
              status: CELL_STATUS.error,
              error: t("hours.quick.outcome_unknown"),
            });
            return;
          }
          const queued = pendingRef.current.get(item.key);
          if (queued) {
            queued.entry.id = null;
            if (queued.entry._destroy) pendingRef.current.delete(item.key);
          }
          updateCell(item.rowId, item.dateKey, {
            hourId: null,
            ...(queued && !queued.entry._destroy ? {} : {
            time: null,
            draft: "",
            description: "",
            }),
            status: CELL_STATUS.saved,
            error: null,
          });
          return;
        }

        // Match saved hour: by id, else by date + activity
        let matched =
          savedList.find((h) => hourJsonId(h) && String(hourJsonId(h)) === String(item.entry.id)) ||
          savedList.find(
            (h) =>
              hourJsonDate(h) === item.dateKey &&
              String(hourJsonPaId(h)) === String(item.entry.project_activity_id)
          );

        // Prefer consuming from front when creates lack id match
        if (!item.entry.id && savedList.length) {
          const idx = savedList.findIndex(
            (h) =>
              hourJsonDate(h) === item.dateKey &&
              String(hourJsonPaId(h) || item.entry.project_activity_id) ===
                String(item.entry.project_activity_id)
          );
          if (idx >= 0) matched = savedList.splice(idx, 1)[0];
        } else if (matched) {
          const idx = savedList.indexOf(matched);
          if (idx >= 0) savedList.splice(idx, 1);
        }

        if (!hourJsonId(matched)) {
          unresolvedRef.current.add(item.key);
          pendingRef.current.delete(item.key);
          updateCell(item.rowId, item.dateKey, {
            status: CELL_STATUS.error,
            error: t("hours.quick.outcome_unknown"),
          });
          return;
        }
        const queued = pendingRef.current.get(item.key);
        if (queued) queued.entry.id = hourJsonId(matched);
        const latest = rowsRef.current.find((row) => row.id === item.rowId)?.cells[item.dateKey];
        const hasNewDraft = queued || (latest && latest.draft !== formatHoursDisplay(item.entry.time, { decimal: useDecimal }));
        updateCell(item.rowId, item.dateKey, {
          hourId: hourJsonId(matched) || item.entry.id,
          ...(hasNewDraft ? {} : {
          time: hourJsonTime(matched) ?? item.entry.time,
          draft: formatHoursDisplay(
            hourJsonTime(matched) ?? item.entry.time,
            { decimal: useDecimal }
          ),
          description: item.entry.description || "",
          }),
          status: queued ? CELL_STATUS.idle : CELL_STATUS.saved,
          error: null,
        });
      });

      setSurfaceStatus(result.errors?.length || unresolvedRef.current.size ? "error" : "saved");
      if (!result.errors?.length && !unresolvedRef.current.size) {
        window.setTimeout(() => {
          setSurfaceStatus((s) => (s === "saved" ? "idle" : s));
        }, 1600);
      }
    } catch (e) {
      items.forEach(({ key, rowId, dateKey }) => {
        unresolvedRef.current.add(key);
        pendingRef.current.delete(key);
        updateCell(rowId, dateKey, {
          status: CELL_STATUS.error,
          error: t("hours.quick.outcome_unknown"),
        });
      });
      setSurfaceStatus("error");
    } finally {
      savingRef.current = false;
      inFlightRef.current.clear();
      if (pendingRef.current.size) scheduleFlush();
    }
  }, [userId, updateCell, t, useDecimal, scheduleFlush]);

  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const flushNow = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    await flushRef.current();
  }, []);

  const commitCell = useCallback(
    (rowId, dateKey, rawValue, { description } = {}) => {
      const row = rowsRef.current.find((r) => r.id === rowId);
      if (!row) return;
      const cell = row.cells[dateKey];
      if (cell?.conflictHours) return;
      const parsed = parseHoursInput(rawValue);
      const desc =
        description != null ? description : cell?.description || "";

      if (parsed == null || parsed === 0) {
        updateCell(rowId, dateKey, {
          time: null,
          draft: "",
          description: desc,
        });
        queueEntry(row, dateKey, { time: 0, description: desc, destroy: true });
        return;
      }

      updateCell(rowId, dateKey, {
        time: parsed,
        draft: formatHoursDisplay(parsed, { decimal: useDecimal }),
        description: desc,
      });
      queueEntry(
        { ...row, cells: { ...row.cells, [dateKey]: { ...cell, time: parsed, description: desc } } },
        dateKey,
        { time: parsed, description: desc, destroy: false }
      );
    },
    [queueEntry, updateCell, useDecimal]
  );

  const focusCell = (rowId, dateKey) => {
    setFocus({ rowId, dateKey });
    const el = gridRef.current?.querySelector(
      `[data-hqe-cell="${rowId}|${dateKey}"]`
    );
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const moveFocus = (rowId, dateKey, dRow, dCol) => {
    const rIdx = rowsRef.current.findIndex((r) => r.id === rowId);
    const cIdx = dayKeys.indexOf(dateKey);
    if (rIdx < 0 || cIdx < 0) return;
    const nr = Math.max(0, Math.min(rowsRef.current.length - 1, rIdx + dRow));
    const nc = Math.max(0, Math.min(dayKeys.length - 1, cIdx + dCol));
    const nextRow = rowsRef.current[nr];
    if (!nextRow) return;
    focusCell(nextRow.id, dayKeys[nc]);
  };

  const onCellKeyDown = (e, rowId, dateKey) => {
    if (e.key === "F2") {
      e.preventDefault();
      e.currentTarget.parentElement.querySelector(".hqe-note-btn")?.click();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      commitCell(rowId, dateKey, e.target.value);
      flushNow();
      moveFocus(rowId, dateKey, 1, 0);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      commitCell(rowId, dateKey, e.target.value);
      moveFocus(rowId, dateKey, 1, 0);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      commitCell(rowId, dateKey, e.target.value);
      moveFocus(rowId, dateKey, -1, 0);
      return;
    }
    if (e.key === "ArrowRight" && e.target.selectionStart === e.target.value.length) {
      e.preventDefault();
      commitCell(rowId, dateKey, e.target.value);
      moveFocus(rowId, dateKey, 0, 1);
      return;
    }
    if (e.key === "ArrowLeft" && e.target.selectionStart === 0) {
      e.preventDefault();
      commitCell(rowId, dateKey, e.target.value);
      moveFocus(rowId, dateKey, 0, -1);
      return;
    }
    if (e.key === "Tab") {
      // Keep spreadsheet navigation independent of save state and note buttons.
      // Blur commits the current cell; the existing queue serializes its save.
      const inputs = Array.from(gridRef.current?.querySelectorAll("[data-hqe-cell]:not(:disabled)") || []);
      const index = inputs.indexOf(e.currentTarget);
      const next = inputs[index + (e.shiftKey ? -1 : 1)];
      if (index >= 0 && next) {
        e.preventDefault();
        next.focus();
        next.select();
        return;
      }
      commitCell(rowId, dateKey, e.target.value);
      flushNow();
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.shiftKey && (e.key === "PageUp" || e.key === "PageDown"))) return;
      if (savingRef.current || pendingRef.current.size) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "textarea") return;
      e.preventDefault();
      flushNow().then(() => {
        setAnchor((prev) =>
          format(shiftWeek(prev, e.key === "PageUp" ? -1 : 1), "yyyy-MM-dd")
        );
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flushNow]);

  useEffect(() => {
    const onUp = () => {
      if (!fill) return;
      const { rowId, dateKey, rangeDates } = fill;
      const row = rowsRef.current.find((r) => r.id === rowId);
      const src = parseFloat(row?.cells?.[dateKey]?.time) || 0;
      const desc = row?.cells?.[dateKey]?.description || "";
      (rangeDates || []).forEach((d) => {
        if (!row?.cells?.[d] || row.cells[d].conflictHours) return;
        updateCell(rowId, d, {
          time: src || null,
          draft: src ? formatHoursDisplay(src, { decimal: useDecimal }) : "",
        });
        if (!src) {
          if (row.cells[d].hourId) {
            queueEntry(row, d, { time: 0, description: desc, destroy: true });
          }
        } else {
          queueEntry(
            {
              ...row,
              cells: {
                ...row.cells,
                [d]: { ...row.cells[d], time: src, description: desc },
              },
            },
            d,
            { time: src, description: desc, destroy: false }
          );
        }
      });
      setFill(null);
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [fill, queueEntry, updateCell, useDecimal]);

  const findRowByPair = useCallback(
    (projectId, activityId) =>
      rowsRef.current.find(
        (r) =>
          String(r.project_id) === String(projectId) &&
          String(r.project_activity_id) === String(activityId)
      ),
    []
  );

  const closePicker = useCallback(() => {
    setPicker(null);
    setPickerItems([]);
    setPickerLoading(false);
  }, []);

  const openActivityPicker = useCallback(
    async ({
      mode = "activity",
      projectId,
      projectActivityId = null,
      rowId = null,
      newProject = null,
      top,
      left,
    }) => {
      const pid = projectId || newProject?.project_id;
      if (!pid) return;
      setPicker({
        mode,
        rowId,
        project_id: pid,
        project_activity_id: projectActivityId,
        newProject,
        top,
        left,
      });
      setPickerLoading(true);
      setPickerItems([]);
      try {
        const [list, groupList] = await Promise.all([
          fetchProjectActivities(pid),
          fetchProjectActivityGroups(pid).catch(() => []),
        ]);
        const raw = Array.isArray(list) ? list : [];
        const groups = Array.isArray(groupList) ? groupList : [];
        const ungroupedLabel = t("project_activity.ungrouped");
        const sections = buildActivityGroupSections(raw, groups, {
          ungroupedLabel,
          hideEmptyGroups: false,
        });
        const ordered = hasRealActivityGroups(raw, groups)
          ? sections.flatMap((section) => section.activities)
          : raw;
        const items = ordered.map((row) => {
          const item = activityPickerItem(row, { ungroupedLabel });
          const existing = findRowByPair(pid, row.id);
          return {
            ...item,
            inGrid: !!existing && (rowId == null || existing.id !== rowId),
          };
        });
        setPickerItems(items);
      } catch {
        setPickerItems([]);
      } finally {
        setPickerLoading(false);
      }
    },
    [findRowByPair, t]
  );

  const openProjectPicker = useCallback(
    async ({ top, left }) => {
      setPicker({ mode: "project", top, left });
      setPickerLoading(true);
      setPickerItems([]);
      try {
        const res = await fetchProjects({
          query: query || undefined,
          per_page: 40,
          page: 1,
          filters: [{ name: "active", value: true }],
        });
        const list = res?.data || [];
        setPickerItems(list.map((p) => projectPickerItem(p)));
      } catch {
        setPickerItems([]);
      } finally {
        setPickerLoading(false);
      }
    },
    [query]
  );

  const addRow = (suggestion, { fromPicker = false } = {}) => {
    if (!suggestion?.project_id || !suggestion?.project_activity_id) return;
    const key = rowKeyFromIds(
      suggestion.project_id,
      suggestion.project_activity_id
    );
    const existing = findRowByPair(
      suggestion.project_id,
      suggestion.project_activity_id
    );
    if (existing) {
      setAddOpen(false);
      setAddNotice(t("hours.quick.row_already_exists"));
      closePicker();
      focusCell(existing.id, dayKeys[0]);
      window.setTimeout(() => setAddNotice(null), 2800);
      return;
    }
    const row = makeEmptyRow(dayKeys, {
      id: key,
      project_id: suggestion.project_id,
      project_activity_id: suggestion.project_activity_id,
      projectTitle: suggestion.projectTitle,
      projectMeta: suggestion.projectMeta,
      activityTitle: suggestion.activityTitle,
    });
    setRows((prev) => [...prev, row]);
    const nextRecent = pushRecentRow({ ...suggestion, tag: "recent" });
    setRecent(nextRecent);
    setAddOpen(false);
    setQuery("");
    setAddNotice(null);
    closePicker();
    if (!fromPicker) {
      // noop — kept for call-site clarity
    }
  };

  const applyActivityPick = (item) => {
    if (!picker || !item?.id) return;
    const activity = item.object || item;
    const activityTitle = activityTitleOf({ project_activity: activity });

    if (picker.mode === "new-activity" || picker.newProject) {
      if (item.inGrid) {
        const existing = findRowByPair(picker.project_id, activity.id);
        if (existing) {
          setAddOpen(false);
          setAddNotice(t("hours.quick.row_already_exists"));
          closePicker();
          focusCell(existing.id, dayKeys[0]);
          window.setTimeout(() => setAddNotice(null), 2800);
          return;
        }
      }
      addRow(
        {
          project_id: picker.newProject?.project_id || picker.project_id,
          project_activity_id: activity.id,
          projectTitle:
            picker.newProject?.projectTitle ||
            projectTitleOf(picker.newProject?.project) ||
            "",
          projectMeta: picker.newProject?.projectMeta || "",
          activityTitle,
        },
        { fromPicker: true }
      );
      return;
    }

    // Change activity on existing row — same project + other activity = new row identity
    const row = rowsRef.current.find((r) => r.id === picker.rowId);
    if (!row) {
      closePicker();
      return;
    }
    const newKey = rowKeyFromIds(row.project_id, activity.id);
    const clash = findRowByPair(row.project_id, activity.id);
    if (clash && clash.id !== row.id) {
      setAddNotice(t("hours.quick.row_already_exists"));
      closePicker();
      focusCell(clash.id, dayKeys[0]);
      window.setTimeout(() => setAddNotice(null), 2800);
      return;
    }

    const hasSaved = dayKeys.some((d) => row.cells[d]?.hourId);
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== picker.rowId) return r;
        return {
          ...r,
          id: newKey,
          project_activity_id: activity.id,
          activityTitle,
        };
      })
    );
    // Re-queue saved cells under new activity so autosave updates them
    if (hasSaved) {
      window.setTimeout(() => {
        const updated = rowsRef.current.find((r) => r.id === newKey);
        if (!updated) return;
        dayKeys.forEach((d) => {
          const cell = updated.cells[d];
          if (!cell?.hourId && !cell?.time) return;
          if (cell.conflictHours) return;
          queueEntry(updated, d, {
            time: parseFloat(cell.time) || 0,
            description: cell.description || "",
            destroy: false,
          });
        });
      }, 0);
    }
    closePicker();
  };

  const removeRow = (rowId) => {
    const row = rowsRef.current.find((r) => r.id === rowId);
    if (!row) return;
    dayKeys.forEach((d) => {
      const cell = row.cells[d];
      if (cell?.hourId && !cell.conflictHours) {
        queueEntry(row, d, { time: 0, destroy: true });
      }
    });
    setRows((prev) => prev.filter((r) => r.id !== rowId));
    flushNow();
  };

  const copyPreviousWeek = async () => {
    if (!userId || copyBusy) return;
    setCopyBusy(true);
    try {
      await flushNow();
      const prevFrom = format(shiftWeek(from, -1), "yyyy-MM-dd");
      const prevTo = format(shiftWeek(to, -1), "yyyy-MM-dd");
      const prevHours = await fetchAllHoursPaginated({
        date_from: prevFrom,
        date_to: prevTo,
        filters: [{ name: "USER", value: userId }],
      });
      const prevBounds = weekBounds(prevFrom);
      const prevKeys = prevBounds.days.map((d) => format(d, "yyyy-MM-dd"));
      const prevRows = buildRowsFromHours(prevHours, prevKeys);

      setRows((current) => {
        const next = [...current];
        const ensureRow = (pr) => {
          let row = next.find(
            (r) =>
              String(r.project_id) === String(pr.project_id) &&
              String(r.project_activity_id) === String(pr.project_activity_id)
          );
          if (!row) {
            row = makeEmptyRow(dayKeys, {
              id: rowKeyFromIds(pr.project_id, pr.project_activity_id),
              project_id: pr.project_id,
              project_activity_id: pr.project_activity_id,
              projectTitle: pr.projectTitle,
              projectMeta: pr.projectMeta,
              activityTitle: pr.activityTitle,
            });
            next.push(row);
          }
          return row;
        };

        prevRows.forEach((pr) => {
          const row = ensureRow(pr);
          prevKeys.forEach((pk, idx) => {
            const targetDate = dayKeys[idx];
            const src = pr.cells[pk];
            const t = parseFloat(src?.time) || 0;
            if (!t || !targetDate) return;
            if (row.cells[targetDate]?.conflictHours) return;
            row.cells[targetDate] = {
              ...row.cells[targetDate],
              time: t,
              draft: formatHoursDisplay(t, { decimal: useDecimal }),
              description: src.description || "",
            };
            queueEntry(row, targetDate, {
              time: t,
              description: src.description || "",
              destroy: false,
            });
          });
        });
        return next.map((r) => ({ ...r, cells: { ...r.cells } }));
      });
      scheduleFlush();
    } finally {
      setCopyBusy(false);
    }
  };

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pairInGrid = (projectId, activityId) =>
      rows.some(
        (r) =>
          String(r.project_id) === String(projectId) &&
          String(r.project_activity_id) === String(activityId)
      );

    const fromRecent = recent
      .filter((r) => {
        if (!q) return true;
        return `${r.projectTitle} ${r.activityTitle}`.toLowerCase().includes(q);
      })
      .map((r) => ({
        ...r,
        tag: t("hours.quick.tag_recent"),
        inGrid: pairInGrid(r.project_id, r.project_activity_id),
      }));

    // Always offer projects so user can pick another activity on an existing project
    const fromProjects = (projectHits || []).flatMap((p) => {
      const title = projectTitleOf(p);
      const meta = projectMetaOf(p);
      if (q && !`${title} ${meta}`.toLowerCase().includes(q)) return [];
      return [
        {
          project_id: p.id,
          project_activity_id: null,
          projectTitle: title,
          projectMeta: meta,
          activityTitle: t("hours.quick.pick_activity_for_project"),
          tag: t("hours.quick.tag_project"),
          needsActivity: true,
          project: p,
          inGrid: false,
        },
      ];
    });

    return [...fromRecent, ...fromProjects].slice(0, 24);
  }, [recent, projectHits, query, t, rows]);

  const saveLabel =
    surfaceStatus === "saving"
      ? t("hours.quick.saving")
      : surfaceStatus === "error"
        ? t("hours.quick.save_error_short")
        : surfaceStatus === "saved"
          ? t("hours.quick.saved")
          : t("hours.quick.save_idle");

  const diff =
    normHours != null ? weekTotal - normHours : null;

  return (
    <div className="hqe hqe-shell">
      <div className="hqe-header">
        <div>
          <div className="hqe-title">{t("hours.quick.title")}</div>
          <div className="hqe-subtitle">
            {t("hours.quick.week_subtitle", {
              week: weekMeta.week,
              range: weekMeta.rangeText,
            })}
            {user?.displayTitle ? ` · ${user.displayTitle}` : ""}
          </div>
        </div>
        <div className="hqe-header-actions">
          <div className="hqe-week-nav">
            <button
              type="button"
              aria-label={t("hours.quick.prev_week")}
              disabled={savingRef.current || pendingRef.current.size > 0}
              onClick={() => {
                flushNow().then(() =>
                  setAnchor(format(shiftWeek(anchor, -1), "yyyy-MM-dd"))
                );
              }}
            >
              ‹
            </button>
            <div className="hqe-week-nav-label">{weekMeta.short}</div>
            <button
              type="button"
              aria-label={t("hours.quick.next_week")}
              disabled={savingRef.current || pendingRef.current.size > 0}
              onClick={() => {
                flushNow().then(() =>
                  setAnchor(format(shiftWeek(anchor, 1), "yyyy-MM-dd"))
                );
              }}
            >
              ›
            </button>
          </div>
          <button
            type="button"
            className="hqe-btn"
            disabled={copyBusy || savingRef.current || pendingRef.current.size > 0}
            onClick={copyPreviousWeek}
          >
            {t("hours.quick.copy_last_week")}
          </button>
          <div
            className={`hqe-save-pill ${
              surfaceStatus === "saving"
                ? "is-saving"
                : surfaceStatus === "error"
                  ? "is-error"
                  : surfaceStatus === "idle"
                    ? "is-idle"
                    : ""
            }`}
            role="status"
            aria-live="polite"
          >
            {saveLabel}
          </div>
        </div>
      </div>

      {batchUnavailable && (
        <div className="hqe-banner">{t("hours.quick.batch_fallback")}</div>
      )}

      {unresolvedRef.current.size > 0 && (
        <div className="hqe-banner" role="alert">
          <p>{t("hours.quick.outcome_unknown")}</p>
          <button type="button" className="hqe-btn" disabled={loading || savingRef.current} onClick={loadWeek}>
            {t("hours.quick.reload_saved")}
          </button>
        </div>
      )}
      {addNotice && !addOpen ? (
        <div className="hqe-banner hqe-banner-info" role="status">
          {addNotice}
        </div>
      ) : null}

      {loading ? (
        <div className="hqe-loading">{t("hours.quick.loading")}</div>
      ) : (
        <div className="hqe-card" ref={gridRef}>
          <div className="hqe-grid hqe-head">
            <div className="hqe-head-label is-start">{t("hours.quick.col_project")}</div>
            <div className="hqe-head-label is-start">{t("hours.quick.col_activity")}</div>
            {dayMeta.map((d) => (
              <div
                key={d.dateKey}
                className={`hqe-day-head${d.isWeekend ? " is-weekend" : ""}${
                  d.isToday ? " is-today" : ""
                }`}
              >
                <div className="hqe-day-label">{d.label}</div>
                <div className="hqe-day-num">{d.num}</div>
              </div>
            ))}
            <div className="hqe-head-label">{t("hours.quick.col_sum")}</div>
            <div />
          </div>

          {rows.map((row) => {
            const rowSum = dayKeys.reduce(
              (s, d) => s + (parseFloat(row.cells[d]?.time) || 0),
              0
            );
            const rowFocused = focus?.rowId === row.id;
            return (
              <div
                key={row.id}
                className={`hqe-grid hqe-row${rowFocused ? " is-focused" : ""}`}
                data-hqe-row={row.id}
                data-project-id={row.project_id || ""}
              >
                <div className="hqe-project">
                  <div className="hqe-project-title" title={row.projectTitle}>
                    {row.projectTitle || t("hours.quick.no_project")}
                  </div>
                  <div className="hqe-project-meta">{row.projectMeta}</div>
                </div>
                <div>
                  <button
                    type="button"
                    className={`hqe-activity-btn${
                      picker?.mode === "activity" && picker?.rowId === row.id
                        ? " is-open"
                        : ""
                    }`}
                    disabled={savingRef.current || pendingRef.current.size > 0}
                    onClick={(e) => {
                      const b = e.currentTarget.getBoundingClientRect();
                      setNoteOpen(null);
                      if (
                        picker?.mode === "activity" &&
                        picker?.rowId === row.id
                      ) {
                        closePicker();
                        return;
                      }
                      const top =
                        b.bottom + 300 > window.innerHeight
                          ? Math.max(8, b.top - 306)
                          : b.bottom + 6;
                      openActivityPicker({
                        mode: "activity",
                        rowId: row.id,
                        projectId: row.project_id,
                        projectActivityId: row.project_activity_id,
                        top,
                        left: b.left,
                      });
                    }}
                  >
                    <span>
                      {row.activityTitle || t("hours.quick.pick_activity")}
                    </span>
                    <span aria-hidden>▾</span>
                  </button>
                </div>
                {dayKeys.map((dateKey, di) => {
                  const cell = row.cells[dateKey];
                  const focused =
                    focus?.rowId === row.id && focus?.dateKey === dateKey;
                  const inFill =
                    fill?.rowId === row.id && fill?.rangeDates?.includes(dateKey);
                  const statusClass =
                    cell?.status === CELL_STATUS.saving
                      ? "is-saving"
                      : cell?.status === CELL_STATUS.saved
                        ? "is-saved"
                        : cell?.status === CELL_STATUS.error ||
                            cell?.status === CELL_STATUS.conflict
                          ? cell?.status === CELL_STATUS.conflict
                            ? "is-conflict"
                            : "is-error"
                          : "";
                  const hasValue = !!(cell?.time || cell?.draft);
                  return (
                    <div
                      key={dateKey}
                      className={`hqe-cell${dayMeta[di].isWeekend ? " is-weekend" : ""}${
                        focused ? " is-focused" : ""
                      }${inFill ? " is-selected" : ""}`}
                      onMouseEnter={() => {
                        if (!fill || fill.rowId !== row.id) return;
                        const startIdx = dayKeys.indexOf(fill.dateKey);
                        const endIdx = di;
                        const a = Math.min(startIdx, endIdx);
                        const b = Math.max(startIdx, endIdx);
                        setFill((f) =>
                          f
                            ? {
                                ...f,
                                rangeDates: dayKeys.slice(a, b + 1),
                              }
                            : f
                        );
                      }}
                    >
                      <input
                        data-hqe-cell={`${row.id}|${dateKey}`}
                        aria-keyshortcuts="F2"
                        className={`hqe-cell-input${hasValue ? " has-value" : ""} ${statusClass}`}
                        value={cell?.draft ?? ""}
                        placeholder="–"
                        disabled={!!cell?.conflictHours || !row.project_activity_id}
                        title={
                          cell?.conflictHours
                            ? t("hours.quick.conflict_hint")
                            : cell?.error || ""
                        }
                        onFocus={() => setFocus({ rowId: row.id, dateKey })}
                        onChange={(e) =>
                          updateCell(row.id, dateKey, { draft: e.target.value })
                        }
                        onBlur={(e) => {
                          commitCell(row.id, dateKey, e.target.value);
                          flushNow();
                        }}
                        onKeyDown={(e) => onCellKeyDown(e, row.id, dateKey)}
                        onDoubleClick={() => {
                          if (cell?.conflictHours && onOpenHour) {
                            const first = cell.conflictHours[0];
                            if (first?.id) onOpenHour(first.id);
                          } else if (cell?.hourId && onOpenHour) {
                            onOpenHour(cell.hourId);
                          }
                        }}
                      />
                      {(hasValue || cell?.description || focused) && (
                        <button
                          type="button"
                          className={`hqe-note-btn${
                            cell?.description ? " has-note" : ""
                          }`}
                          title={t("hours.quick.description")}
                          onClick={(e) => {
                            const b = e.currentTarget.getBoundingClientRect();
                            const top =
                              b.bottom + 180 > window.innerHeight
                                ? Math.max(8, b.top - 186)
                                : b.bottom + 8;
                            setNoteOpen({
                              rowId: row.id,
                              dateKey,
                              top,
                              left: Math.min(
                                window.innerWidth - 272,
                                Math.max(8, b.left - 130)
                              ),
                            });
                            setNoteDraft(cell?.description || "");
                          }}
                        >
                          ✎
                        </button>
                      )}
                      {focused && !fill && row.project_activity_id && (
                        <div
                          className="hqe-fill-handle"
                          title={t("hours.quick.fill_handle")}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setFill({
                              rowId: row.id,
                              dateKey,
                              rangeDates: [dateKey],
                            });
                          }}
                        />
                      )}
                    </div>
                  );
                })}
                <div className={`hqe-sum${rowSum ? "" : " is-empty"}`}>
                  {formatHoursSum(rowSum)}
                </div>
                <div style={{ display: "grid", placeItems: "center" }}>
                  <button
                    type="button"
                    className="hqe-remove"
                    disabled={savingRef.current || pendingRef.current.size > 0}
                    title={t("hours.quick.remove_row")}
                    onClick={() => removeRow(row.id)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}

          <div className="hqe-grid hqe-foot">
            <div style={{ gridColumn: "span 2" }}>
              <button
                type="button"
                className="hqe-new-row-btn"
                onClick={() => {
                  setAddOpen((v) => !v);
                  setAddNotice(null);
                  closePicker();
                }}
              >
                <span>+</span>
                {t("hours.quick.new_row")}
              </button>
            </div>
            {dayTotals.map((tot, i) => (
              <div
                key={dayKeys[i]}
                className={`hqe-day-total${
                  dayMeta[i].isWeekend ? " is-weekend" : ""
                }${tot > 9 ? " is-over" : ""}${tot ? "" : " is-empty"}`}
              >
                {formatHoursSum(tot)}
              </div>
            ))}
            <div className="hqe-sum">{formatHoursSum(weekTotal)}</div>
            <div />
          </div>
        </div>
      )}

      {addOpen && (
        <div className="hqe-add-panel">
          <input
            className="hqe-add-search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("hours.quick.search_placeholder")}
          />
          {addNotice ? (
            <div className="hqe-add-notice" role="status">
              {addNotice}
            </div>
          ) : null}
          <div className="hqe-add-label">
            {query
              ? t("hours.quick.suggestions_hits")
              : t("hours.quick.suggestions_recent")}
          </div>
          <div className="hqe-suggest">
            {suggestions.map((s, idx) => (
              <button
                key={`${s.project_id}-${s.project_activity_id}-${idx}`}
                type="button"
                className={`hqe-suggest-item${s.inGrid ? " is-in-grid" : ""}`}
                onClick={(e) => {
                  if (s.inGrid && !s.needsActivity) {
                    const existing = findRowByPair(
                      s.project_id,
                      s.project_activity_id
                    );
                    if (existing) {
                      setAddOpen(false);
                      setAddNotice(t("hours.quick.row_already_exists"));
                      focusCell(existing.id, dayKeys[0]);
                      window.setTimeout(() => setAddNotice(null), 2800);
                      return;
                    }
                  }
                  if (s.needsActivity) {
                    const b = e.currentTarget.getBoundingClientRect();
                    const top =
                      b.bottom + 300 > window.innerHeight
                        ? Math.max(8, b.top - 306)
                        : b.bottom + 6;
                    openActivityPicker({
                      mode: "new-activity",
                      projectId: s.project_id,
                      newProject: s,
                      top,
                      left: Math.min(window.innerWidth - 320, b.left),
                    });
                    return;
                  }
                  addRow(s);
                }}
              >
                <span>
                  <div className="hqe-suggest-project">{s.projectTitle}</div>
                  <div className="hqe-suggest-activity">{s.activityTitle}</div>
                </span>
                <span className="hqe-suggest-tag">
                  {s.inGrid ? t("hours.quick.tag_in_grid") : s.tag}
                </span>
              </button>
            ))}
            {!suggestions.length && (
              <div className="hqe-loading">{t("hours.quick.no_suggestions")}</div>
            )}
          </div>
          <div style={{ marginTop: 14 }}>
            <div className="hqe-add-label">{t("hours.quick.add_via_selectors")}</div>
            <button
              type="button"
              className="hqe-browse-projects-btn"
              onClick={(e) => {
                const b = e.currentTarget.getBoundingClientRect();
                const top =
                  b.bottom + 320 > window.innerHeight
                    ? Math.max(8, b.top - 326)
                    : b.bottom + 6;
                openProjectPicker({ top, left: b.left });
              }}
            >
              <span>{t("hours.quick.browse_projects")}</span>
              <span aria-hidden>▾</span>
            </button>
            <div className="hqe-add-hint">{t("hours.quick.add_row_hint")}</div>
          </div>
        </div>
      )}

      <div className="hqe-footer">
        <div className="hqe-norm">
          <div>
            <div className="hqe-norm-label">{t("hours.quick.norm_title")}</div>
            <div className="hqe-norm-value">
              <strong>{formatHoursSum(weekTotal)}</strong>
              <span>
                {normHours != null
                  ? t("hours.quick.norm_of", { hours: formatHoursSum(normHours) })
                  : t("hours.quick.norm_unknown")}
              </span>
            </div>
          </div>
          {normHours != null && (
            <>
              <div className="hqe-progress">
                <i
                  className={diff > 0 ? "is-over" : ""}
                  style={{
                    width: `${Math.min(100, (weekTotal / Math.max(normHours, 0.1)) * 100)}%`,
                  }}
                />
              </div>
              <div className={`hqe-diff${diff > 0 ? " is-over" : ""}`}>
                {diff === 0
                  ? t("hours.quick.norm_on_target")
                  : diff > 0
                    ? t("hours.quick.norm_over", { hours: formatHoursSum(diff) })
                    : t("hours.quick.norm_left", {
                        hours: formatHoursSum(Math.abs(diff)),
                      })}
              </div>
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div className="hqe-hint">{t("hours.quick.keyboard_hint")}</div>
          <button type="button" className="hqe-btn" onClick={() => flushNow()}>
            {t("hours.quick.flush_now")}
          </button>
        </div>
      </div>

      {noteOpen && (
        <div
          className="hqe-popover"
          style={{ top: noteOpen.top, left: noteOpen.left }}
        >
          <div className="hqe-popover-title">{t("hours.quick.description")}</div>
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder={t("hours.quick.description_placeholder")}
            autoFocus
          />
          <div className="hqe-popover-actions">
            <button
              type="button"
              className="hqe-btn hqe-btn-primary"
              style={{ height: 32, padding: "0 14px", fontSize: 12.5 }}
              onClick={() => {
                const { rowId, dateKey } = noteOpen;
                const row = rowsRef.current.find((r) => r.id === rowId);
                const cell = row?.cells?.[dateKey];
                updateCell(rowId, dateKey, { description: noteDraft });
                if (cell?.time || cell?.hourId) {
                  commitCell(rowId, dateKey, cell.draft || formatHoursDisplay(cell.time, { decimal: useDecimal }), {
                    description: noteDraft,
                  });
                  flushNow();
                }
                setNoteOpen(null);
              }}
            >
              {t("hours.quick.description_done")}
            </button>
          </div>
        </div>
      )}

      {picker && (
        <QuickListPicker
          title={
            picker.mode === "project"
              ? t("hours.quick.pick_project")
              : t("hours.quick.pick_activity")
          }
          items={pickerItems}
          loading={pickerLoading}
          selectedId={
            picker.mode === "project" ? null : picker.project_activity_id
          }
          searchPlaceholder={
            picker.mode === "project"
              ? t("hours.quick.search_projects")
              : t("hours.quick.search_activities")
          }
          emptyLabel={
            picker.mode === "project"
              ? t("hours.quick.no_projects")
              : t("hours.quick.no_activities")
          }
          top={picker.top}
          left={picker.left}
          onClose={closePicker}
          onPick={(item) => {
            if (picker.mode === "project") {
              const project = item.object;
              if (!project?.id) return;
              openActivityPicker({
                mode: "new-activity",
                projectId: project.id,
                newProject: {
                  project_id: project.id,
                  projectTitle: projectTitleOf(project),
                  projectMeta: projectMetaOf(project),
                  project,
                },
                top: picker.top,
                left: picker.left,
              });
              return;
            }
            applyActivityPick(item);
          }}
        />
      )}
    </div>
  );
}

export function HoursEntryModeToggle({ mode, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="hqe-mode-toggle" role="group" aria-label={t("hours.quick.mode_aria")}>
      <button
        type="button"
        className={mode === "form" ? "is-active" : ""}
        onClick={() => onChange("form")}
      >
        {t("hours.quick.mode_form")}
      </button>
      <button
        type="button"
        className={mode === "quick" ? "is-active" : ""}
        onClick={() => onChange("quick")}
      >
        {t("hours.quick.mode_quick")}
      </button>
    </div>
  );
}
